import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/providers/AuthProvider';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Home,
  Gavel,
  Palette,
  BookOpen,
  Radio,
  Phone,
  User,
  Settings,
  Star,
  FileText,
  Shield,
  X,
  LogIn,
  LogOut,
  LayoutDashboard,
  Clock,
  Heart,
  Users,
  Mail,
  HelpCircle,
  TrendingUp,
  Zap,
  Award,
  Globe,
  MessageCircle,
  CreditCard
} from 'lucide-react';

interface FullScreenMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

const FullScreenMenu: React.FC<FullScreenMenuProps> = ({ isOpen, onClose }) => {
  const { user, isAdmin, signOut } = useAuth();
  const navigate = useNavigate();

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = 'unset';
      };
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleNavigation = (path: string) => {
    navigate(path);
    onClose();
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      onClose();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Primary navigation - Core platform features
  const primaryItems = [
    { 
      path: '/', 
      label: 'Home', 
      icon: Home, 
      gradient: 'from-emerald-600/90 via-teal-600/90 to-green-700/90',
      description: 'Dashboard & Overview'
    },
    { 
      path: '/auctions', 
      label: 'Auctions', 
      icon: Gavel, 
      gradient: 'from-blue-600/90 via-indigo-600/90 to-blue-700/90',
      description: 'Live & Upcoming Sales'
    },
    { 
      path: '/artworks', 
      label: 'Collection', 
      icon: Palette, 
      gradient: 'from-teal-600/90 via-cyan-600/90 to-teal-700/90',
      description: 'Artwork Gallery'
    },
    { 
      path: '/broadcast', 
      label: 'Live Stream', 
      icon: Radio, 
      gradient: 'from-red-600/90 via-pink-600/90 to-red-700/90',
      description: 'Active Bidding'
    },
  ];

  // Secondary navigation - Supporting features
  const secondaryItems = [
    { 
      path: '/blog', 
      label: 'Editorial', 
      icon: BookOpen, 
      gradient: 'from-purple-600/80 via-indigo-600/80 to-purple-700/80'
    },
    { 
      path: '/artists', 
      label: 'Artists', 
      icon: Users, 
      gradient: 'from-green-600/80 via-emerald-600/80 to-green-700/80'
    },
    { 
      path: '/contact', 
      label: 'Contact', 
      icon: Phone, 
      gradient: 'from-orange-600/80 via-amber-600/80 to-orange-700/80'
    },
    { 
      path: '/faq', 
      label: 'Support', 
      icon: HelpCircle, 
      gradient: 'from-blue-600/80 via-cyan-600/80 to-blue-700/80'
    },
  ];

  // User-specific navigation
  const userItems = user ? [
    { 
      path: '/dashboard', 
      label: 'Dashboard', 
      icon: LayoutDashboard, 
      gradient: 'from-slate-600/80 via-gray-600/80 to-slate-700/80'
    },
    { 
      path: '/profile', 
      label: 'Profile', 
      icon: User, 
      gradient: 'from-blue-600/80 via-indigo-600/80 to-blue-700/80'
    },
    { 
      path: '/appraisal', 
      label: 'Appraisals', 
      icon: Star, 
      gradient: 'from-amber-600/80 via-yellow-600/80 to-amber-700/80'
    },
    { 
      path: '/consign', 
      label: 'Consignments', 
      icon: TrendingUp, 
      gradient: 'from-green-600/80 via-emerald-600/80 to-green-700/80'
    },
  ] : [];

  // Admin navigation
  const adminItems = isAdmin ? [
    { 
      path: '/admin', 
      label: 'Administration', 
      icon: Shield, 
      gradient: 'from-red-600/80 via-orange-600/80 to-red-700/80'
    },
  ] : [];

  // Footer navigation - Always visible
  const footerItems = [
    { 
      path: '/privacy', 
      label: 'Privacy', 
      icon: FileText, 
      gradient: 'from-gray-600/80 via-slate-600/80 to-gray-700/80'
    },
    { 
      path: '/terms', 
      label: 'Terms', 
      icon: FileText, 
      gradient: 'from-gray-600/80 via-slate-600/80 to-gray-700/80'
    },
    { 
      path: '/cookies', 
      label: 'Cookies', 
      icon: FileText, 
      gradient: 'from-gray-600/80 via-slate-600/80 to-gray-700/80'
    },
    { 
      path: '/sitemap', 
      label: 'Sitemap', 
      icon: Globe, 
      gradient: 'from-gray-600/80 via-slate-600/80 to-gray-700/80'
    },
  ];

  return (
    <div 
      className={`fixed inset-0 z-[9999] w-full h-full transition-all duration-700 ${
        isOpen ? 'pointer-events-auto' : 'pointer-events-none'
      }`}
    >
      {/* Animated Background Layers */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Base gradient background */}
        <div 
          className={`absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 transition-opacity duration-500 ${
            isOpen ? 'opacity-100' : 'opacity-0'
          }`}
        />
        
        {/* Moving animated background */}
        <div 
          className={`absolute inset-0 transition-opacity duration-700 ${
            isOpen ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {/* Animated gradient orbs */}
          <div className="absolute inset-0">
            <div 
              className="absolute top-1/4 -left-20 w-[600px] h-[600px] bg-gradient-to-br from-emerald-500/20 via-teal-500/10 to-transparent rounded-full blur-3xl"
              style={{
                animationName: isOpen ? 'float' : 'none',
                animationDuration: '20s',
                animationTimingFunction: 'ease-in-out',
                animationIterationCount: 'infinite',
              }}
            />
            <div 
              className="absolute bottom-1/4 -right-20 w-[600px] h-[600px] bg-gradient-to-br from-blue-500/20 via-indigo-500/10 to-transparent rounded-full blur-3xl"
              style={{
                animationName: isOpen ? 'float' : 'none',
                animationDuration: '25s',
                animationTimingFunction: 'ease-in-out',
                animationIterationCount: 'infinite',
                animationDirection: 'reverse',
                animationDelay: '2s'
              }}
            />
            <div 
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-br from-purple-500/10 via-pink-500/5 to-transparent rounded-full blur-3xl"
              style={{
                animationName: isOpen ? 'pulse' : 'none',
                animationDuration: '15s',
                animationTimingFunction: 'ease-in-out',
                animationIterationCount: 'infinite',
                animationDelay: '1s'
              }}
            />
          </div>
          
          {/* Animated mesh gradient */}
          <div 
            className="absolute inset-0 opacity-30"
            style={{
              background: `
                linear-gradient(45deg, transparent 30%, rgba(16, 185, 129, 0.1) 50%, transparent 70%),
                linear-gradient(135deg, transparent 30%, rgba(59, 130, 246, 0.1) 50%, transparent 70%)
              `,
              backgroundSize: '200% 200%',
              animationName: isOpen ? 'gradientShift' : 'none',
              animationDuration: '15s',
              animationTimingFunction: 'ease',
              animationIterationCount: 'infinite',
            }}
          />
          
          {/* Floating particles */}
          <div className="absolute inset-0">
            {[...Array(20)].map((_, i) => (
              <div
                key={`particle-${i}`}
                className="absolute w-1 h-1 bg-white/20 rounded-full"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationName: isOpen ? 'floatUp' : 'none',
                  animationDuration: `${10 + Math.random() * 20}s`,
                  animationTimingFunction: 'linear',
                  animationIterationCount: 'infinite',
                  animationDelay: `${Math.random() * 10}s`,
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Content Container */}
      <div className={`relative h-screen w-screen transition-all duration-700 ease-out ${
        isOpen
          ? 'transform translate-y-0 opacity-100' 
          : 'transform translate-y-4 opacity-0'
      }`}>
        
        {/* Premium Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 lg:p-8 bg-white/5 backdrop-blur-xl border-b border-white/10">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-2xl">
                <span className="text-white font-semibold text-lg">F</span>
              </div>
              <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-emerald-400/20 to-teal-500/20 blur-sm" />
            </div>
            <div>
              <h1 className="text-2xl font-light text-white tracking-wide">Florida Highwaymen</h1>
              <p className="text-emerald-300/80 text-sm font-light tracking-wider">NAVIGATION MENU</p>
            </div>
          </div>
          
          <Button
            onClick={onClose}
            variant="ghost"
            size="sm"
            className="h-12 w-12 p-0 text-white/80 hover:text-white hover:bg-white/10 border border-white/20 hover:border-white/30 rounded-xl transition-all duration-300"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Professional Grid Layout */}
        <div className="p-4 sm:p-6 lg:p-8 overflow-y-auto max-h-[calc(100vh-112px)] min-h-[calc(100vh-112px)]">
          <div className="max-w-7xl mx-auto space-y-6 lg:space-y-8 pb-8">
            
            {/* Primary Navigation - Featured Cards with Staggered Animation */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
              {primaryItems.map((item, index) => (
                <div
                  key={item.path}
                  onClick={() => handleNavigation(item.path)}
                  className="group cursor-pointer"
                  style={{
                    opacity: isOpen ? 1 : 0,
                    transform: isOpen ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.9)',
                    transition: 'all 600ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                    transitionDelay: `${600 + index * 100}ms`
                  }}
                >
                  <div className={`
                    relative h-40 rounded-2xl bg-gradient-to-br ${item.gradient}
                    backdrop-blur-xl border border-white/20 
                    hover:border-white/40 hover:shadow-2xl hover:shadow-black/25
                    transform transition-all duration-300 ease-out
                    hover:scale-[1.02] hover:-translate-y-1
                    flex flex-col items-center justify-center text-white
                    overflow-hidden
                  `}>
                    {/* Glass morphism overlay */}
                    <div className="absolute inset-0 bg-white/10 backdrop-blur-sm" />
                    
                    {/* Content */}
                    <div className="relative z-10 text-center space-y-3">
                      <div className="mx-auto w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                        <item.icon className="h-6 w-6" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg tracking-wide">{item.label}</h3>
                        <p className="text-xs text-white/80 font-light tracking-wider uppercase">{item.description}</p>
                      </div>
                    </div>

                    {/* Subtle glow effect */}
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                </div>
              ))}
            </div>

            {/* Secondary Navigation - Refined Cards with Staggered Animation */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
              {secondaryItems.map((item, index) => (
                <div
                  key={item.path}
                  onClick={() => handleNavigation(item.path)}
                  className="group cursor-pointer"
                  style={{
                    opacity: isOpen ? 1 : 0,
                    transform: isOpen ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.9)',
                    transition: 'all 600ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                    transitionDelay: `${1000 + index * 50}ms`
                  }}
                >
                  <div className={`
                    relative h-28 rounded-xl bg-gradient-to-br ${item.gradient}
                    backdrop-blur-xl border border-white/10 
                    hover:border-white/30 hover:shadow-xl hover:shadow-black/20
                    transform transition-all duration-300 ease-out
                    hover:scale-[1.02]
                    flex flex-col items-center justify-center text-white
                  `}>
                    <div className="absolute inset-0 bg-white/5 backdrop-blur-sm rounded-xl" />
                    
                    <div className="relative z-10 text-center space-y-2">
                      <div className="mx-auto w-10 h-10 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center">
                        <item.icon className="h-5 w-5" />
                      </div>
                      <h4 className="font-medium text-sm tracking-wide">{item.label}</h4>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* User & Admin Navigation */}
            {(userItems.length > 0 || adminItems.length > 0) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
                {[...userItems, ...adminItems].map((item, index) => (
                  <div
                    key={item.path}
                    onClick={() => handleNavigation(item.path)}
                    className="group cursor-pointer"
                    style={{
                      opacity: isOpen ? 1 : 0,
                      transform: isOpen ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.9)',
                      transition: 'all 600ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                      transitionDelay: `${1400 + index * 50}ms`
                    }}
                  >
                    <div className={`
                      relative h-24 rounded-xl bg-gradient-to-br ${item.gradient}
                      backdrop-blur-xl border border-white/10 
                      hover:border-white/30 hover:shadow-lg hover:shadow-black/15
                      transform transition-all duration-300 ease-out
                      hover:scale-[1.02]
                      flex items-center justify-center text-white
                    `}>
                      <div className="absolute inset-0 bg-white/5 backdrop-blur-sm rounded-xl" />
                      
                      <div className="relative z-10 flex items-center space-x-3">
                        <div className="w-9 h-9 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center">
                          <item.icon className="h-4 w-4" />
                        </div>
                        <h4 className="font-medium text-sm">{item.label}</h4>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Footer Navigation */}
            <div className="flex justify-center">
              <div className="flex flex-wrap justify-center gap-4">
                {footerItems.map((item, index) => (
                  <button
                    key={item.path}
                    onClick={() => handleNavigation(item.path)}
                    className="px-4 py-2 text-sm text-white/60 hover:text-white transition-all duration-300 hover:bg-white/5 rounded-lg border border-transparent hover:border-white/20"
                    style={{
                      opacity: isOpen ? 1 : 0,
                      transform: isOpen ? 'translateY(0)' : 'translateY(20px)',
                      transition: 'all 600ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                      transitionDelay: `${1600 + index * 100}ms`
                    }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Login/Register for non-authenticated users */}
            {!user && (
              <div className="flex justify-center pt-6">
                <div className="flex space-x-4">
                  <Button
                    onClick={() => handleNavigation('/login')}
                    className="bg-white/10 text-white border border-white/20 hover:bg-white/20 hover:border-white/40 rounded-lg px-6 py-2 font-light tracking-wide transition-all duration-300 backdrop-blur-sm"
                    style={{
                      opacity: isOpen ? 1 : 0,
                      transform: isOpen ? 'translateY(0)' : 'translateY(20px)',
                      transition: 'all 600ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                      transitionDelay: '1800ms'
                    }}
                  >
                    <LogIn className="h-4 w-4 mr-2" />
                    Login
                  </Button>
                </div>
              </div>
            )}

            {/* Elegant Sign Out */}
            {user && (
              <div className="flex justify-center pt-6">
                <Button
                  onClick={handleSignOut}
                  variant="ghost"
                  className="text-white/60 hover:text-white hover:bg-white/5 border border-white/10 hover:border-white/20 rounded-lg px-6 py-2 font-light tracking-wide transition-all duration-300"
                  style={{
                    opacity: isOpen ? 1 : 0,
                    transform: isOpen ? 'translateY(0)' : 'translateY(20px)',
                    transition: 'all 600ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                    transitionDelay: '1800ms'
                  }}
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign Out
                </Button>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Add animation keyframes */}
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
        
        @keyframes float {
          0%, 100% { 
            transform: translate(0, 0) rotate(0deg) scale(1); 
          }
          25% { 
            transform: translate(100px, -100px) rotate(90deg) scale(1.1); 
          }
          50% { 
            transform: translate(-50px, -200px) rotate(180deg) scale(0.9); 
          }
          75% { 
            transform: translate(-150px, -50px) rotate(270deg) scale(1.05); 
          }
        }
        
        @keyframes pulse {
          0%, 100% { 
            transform: translate(-50%, -50%) scale(1); 
            opacity: 0.1;
          }
          50% { 
            transform: translate(-50%, -50%) scale(1.2); 
            opacity: 0.2;
          }
        }
        
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        @keyframes floatUp {
          0% { 
            transform: translateY(100vh) translateX(0);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% { 
            transform: translateY(-100vh) translateX(100px);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default FullScreenMenu;